import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import {
  ArrowLeft,
  LogOut,
  RefreshCcw,
  Coins,
  Activity,
  Package,
  ShieldAlert,
  Hourglass,
  Globe,
  MessageSquare,
  Calculator,
  Key,
  Copy,
  Check,
  Trash2,
  Plus,
} from "lucide-react";
import {
  adminCreateApiKey,
  adminCves,
  adminFeedback,
  adminJobs,
  adminListApiKeys,
  adminMe,
  adminRecomputeExploitability,
  adminRevokeApiKey,
  adminSboms,
  adminStats,
  adminVisits,
} from "../lib/api";
import { clearAdminToken, getAdminToken } from "../lib/adminAuth";
import { severityClass, fmtDate } from "../lib/utils";
import ChangePasswordDialog from "../components/ChangePasswordDialog";

export default function AdminDashboard() {
  const nav = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [stats, setStats] = useState(null);
  const [cves, setCves] = useState([]);
  const [sboms, setSboms] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [visits, setVisits] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [me, st, c, s, j, v, f] = await Promise.all([
        adminMe(),
        adminStats(),
        adminCves(50),
        adminSboms(50),
        adminJobs(50),
        adminVisits(30).catch(() => null),
        adminFeedback(100).catch(() => []),
      ]);
      setAdmin(me);
      setStats(st);
      setCves(c);
      setSboms(s);
      setJobs(j);
      setVisits(v);
      setFeedback(f || []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        toast.error("Session expired", { description: "Please sign in again." });
        clearAdminToken();
        nav("/cursedgold45/login", { replace: true });
      } else {
        toast.error("Couldn't load admin data", {
          description: err?.response?.data?.detail || err?.message,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!getAdminToken()) {
      nav("/cursedgold45/login", { replace: true });
      return;
    }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = () => {
    clearAdminToken();
    toast.info("Signed out");
    nav("/cursedgold45/login", { replace: true });
  };

  return (
    <div className="App">
      <Toaster
        theme="light"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            color: "#0f172a",
          },
        }}
      />
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-3 flex flex-wrap items-center gap-2 md:gap-4">
          <Link
            to="/"
            className="flex items-center gap-2 text-slate-700 hover:text-violet-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="font-display font-bold tracking-tight">
              CVE Analytics
            </span>
          </Link>
          <div className="overline hidden sm:block">Admin · owner console</div>
          <div className="flex-1" />
          {admin && (
            <span className="hidden md:inline font-mono text-xs text-slate-600">
              {admin.email}
            </span>
          )}
          <button
            onClick={loadAll}
            data-testid="admin-refresh"
            className="flex items-center gap-1.5 text-xs font-mono text-slate-700 hover:text-violet-700 border border-slate-200 hover:border-violet-300 rounded-md px-3 py-1.5 transition-colors"
          >
            <RefreshCcw className="h-3.5 w-3.5" />
            Refresh
          </button>
          <RecomputeButton onDone={loadAll} />
          <ChangePasswordDialog />
          <button
            onClick={signOut}
            data-testid="admin-logout"
            className="flex items-center gap-1.5 text-xs font-mono text-slate-700 hover:text-red-700 border border-slate-200 hover:border-red-300 rounded-md px-3 py-1.5 transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" />
            Sign out
          </button>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 md:px-8 py-8 md:py-10 space-y-6">
        {loading && !stats && (
          <div className="panel p-7">
            <div className="overline mb-2">Loading…</div>
            <div className="font-mono text-violet-700 text-sm">
              Fetching analytics<span className="caret">&nbsp;</span>
            </div>
          </div>
        )}

        {stats && (
          <>
            <CreditsPanel credits={stats.credits} />
            <StatsGrid stats={stats} />
            <VisitsPanel visits={visits} />
            <ApiKeysPanel />
            <CvesTable rows={cves} />
            <SbomTable rows={sboms} />
            <JobsTable rows={jobs} />
            <FeedbackTable rows={feedback} />
          </>
        )}
      </main>
    </div>
  );
}

function CreditsPanel({ credits }) {
  const pct = Math.max(0, Math.min(100, credits.pct_spent ?? 0));
  const tone =
    pct >= 80 ? "text-red-700" : pct >= 50 ? "text-orange-700" : "text-emerald-700";
  return (
    <section className="panel p-6 md:p-7" data-testid="admin-credits">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="overline flex items-center gap-2">
            <Coins className="h-3 w-3 text-amber-700" />
            LLM credits · estimated
          </div>
          <h3 className="font-display text-xl text-slate-900 mt-1">
            Approximate spend
          </h3>
        </div>
        <span className="text-[10px] font-mono text-amber-700 border border-amber-200 bg-amber-50 px-2 py-1 rounded-sm">
          ESTIMATE · per-call avg
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Stat label="Spent (est.)" value={`$${credits.estimated_spent_usd}`} tone="text-slate-900" />
        <Stat
          label="Remaining"
          value={`$${credits.remaining_usd}`}
          tone={tone}
        />
        <Stat label="Budget" value={`$${credits.budget_usd}`} />
        <Stat label="% spent" value={`${credits.pct_spent}%`} tone={tone} />
      </div>

      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-3">
        <div
          className="h-full transition-all duration-700"
          style={{
            width: `${pct}%`,
            background:
              "#7c3aed",
          }}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-slate-600">
        <span>
          Analyze calls:{" "}
          <span className="text-slate-900 font-mono">
            {credits.estimated_calls.analyze}
          </span>{" "}
          × ${credits.cost_per_analyze_usd}/call
        </span>
        <span>
          Detection calls:{" "}
          <span className="text-slate-900 font-mono">
            {credits.estimated_calls.detections}
          </span>{" "}
          × ${credits.cost_per_detections_usd}/call
        </span>
      </div>
      <p className="text-[11px] font-mono text-slate-500 mt-3">
        Budget knob: <code className="text-violet-700">LLM_BUDGET_USD</code> in backend .env
      </p>
    </section>
  );
}

function StatsGrid({ stats }) {
  const t = stats.totals;
  const a = stats.activity;
  return (
    <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-stats-grid">
      <Card icon={ShieldAlert} label="CVEs analyzed" value={t.cves_analyzed} hint={`${t.cves_with_detections} have detections`} />
      <Card icon={Package} label="SBOM scans" value={t.sbom_scans} hint={`${t.sbom_findings} total findings`} />
      <Card icon={Activity} label="CVEs · last 24h" value={a.cves_last_24h} hint={`${a.cves_last_7d} in 7d`} />
      <Card icon={Hourglass} label="Jobs · by status" value={Object.values(t.jobs_by_status || {}).reduce((x, y) => x + y, 0)} hint={Object.entries(t.jobs_by_status || {}).map(([k, v]) => `${k}:${v}`).join(" · ")} />
    </section>
  );
}

function Card({ icon: Icon, label, value, hint }) {
  return (
    <div className="panel p-5">
      <div className="overline flex items-center gap-1.5">
        <Icon className="h-3 w-3 text-violet-700" />
        {label}
      </div>
      <div className="font-mono font-bold text-3xl mt-2 text-slate-900">
        {value ?? "-"}
      </div>
      {hint && (
        <div className="text-[10px] font-mono text-slate-500 mt-1 truncate">
          {hint}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone = "text-slate-900" }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
      <div className="overline">{label}</div>
      <div className={`font-mono font-bold text-2xl mt-1 ${tone}`}>{value}</div>
    </div>
  );
}

function CvesTable({ rows }) {
  return (
    <section className="panel p-5" data-testid="admin-cves-table">
      <div className="overline mb-3">Analyzed CVEs · newest {rows.length}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-slate-500">
            <tr className="border-b border-slate-100">
              <Th>CVE</Th>
              <Th>Severity</Th>
              <Th>Priority</Th>
              <Th>KEV</Th>
              <Th>Detections</Th>
              <Th>Analyzed at</Th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="py-4 text-slate-500 font-mono">- empty -</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-100">
                <Td><span className="font-mono text-violet-700">{r.id}</span></Td>
                <Td>
                  {r.severity ? (
                    <span className={`whitespace-nowrap text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 border rounded-sm ${severityClass(r.severity)}`}>
                      {r.severity} · {r.score}
                    </span>
                  ) : "-"}
                </Td>
                <Td>
                  {r.priority_tier ? (
                    <span className="whitespace-nowrap font-mono text-slate-700">{r.priority_tier} · {r.priority_score}</span>
                  ) : "-"}
                </Td>
                <Td>
                  {r.in_kev ? (
                    <span className="text-red-700 text-[10px] font-bold uppercase tracking-wider">YES</span>
                  ) : <span className="text-slate-500">-</span>}
                </Td>
                <Td>
                  {r.has_detections ? (
                    <span className="text-emerald-700 text-[10px] font-bold uppercase tracking-wider">READY</span>
                  ) : <span className="text-slate-500">-</span>}
                </Td>
                <Td><span className="font-mono text-slate-600">{fmtDate(r.cached_at)}</span></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SbomTable({ rows }) {
  return (
    <section className="panel p-5" data-testid="admin-sboms-table">
      <div className="overline mb-3">SBOM scans · newest {rows.length}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-slate-500">
            <tr className="border-b border-slate-100">
              <Th>Project</Th>
              <Th>Components</Th>
              <Th>Vulnerable</Th>
              <Th>Findings</Th>
              <Th>With CVE</Th>
              <Th>Created</Th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="py-4 text-slate-500 font-mono">- empty -</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.scan_id} className="border-b border-slate-100 hover:bg-slate-100">
                <Td><span className="font-mono text-slate-900">{r.name}</span></Td>
                <Td><span className="font-mono">{r.stats.components ?? 0}</span></Td>
                <Td><span className="font-mono text-orange-700">{r.stats.vulnerable_components ?? 0}</span></Td>
                <Td><span className="font-mono">{r.stats.total_findings ?? 0}</span></Td>
                <Td><span className="font-mono">{r.stats.with_cve ?? 0}</span></Td>
                <Td><span className="font-mono text-slate-600">{fmtDate(r.created_at)}</span></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function JobsTable({ rows }) {
  const statusTone = {
    done: "text-emerald-700",
    failed: "text-red-700",
    cancelled: "text-slate-600",
    running: "text-violet-700",
    queued: "text-slate-700",
  };
  return (
    <section className="panel p-5" data-testid="admin-jobs-table">
      <div className="overline mb-3">Async jobs · newest {rows.length}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-slate-500">
            <tr className="border-b border-slate-100">
              <Th>Job</Th>
              <Th>Type</Th>
              <Th>Target</Th>
              <Th>Status</Th>
              <Th>Created</Th>
              <Th>Finished</Th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="py-4 text-slate-500 font-mono">- empty -</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.job_id} className="border-b border-slate-100 hover:bg-slate-100">
                <Td><span className="font-mono text-slate-600 text-[10px]">{r.job_id.slice(0, 10)}…</span></Td>
                <Td><span className="font-mono">{r.type}</span></Td>
                <Td><span className="font-mono text-violet-700">{r.target}</span></Td>
                <Td>
                  <span className={`font-mono uppercase text-[10px] tracking-wider font-bold ${statusTone[r.status] || "text-slate-700"}`}>
                    {r.status}
                  </span>
                </Td>
                <Td><span className="font-mono text-slate-600">{fmtDate(r.created_at)}</span></Td>
                <Td><span className="font-mono text-slate-600">{fmtDate(r.finished_at)}</span></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Th({ children }) {
  return <th className="text-left font-mono uppercase tracking-wider text-[10px] py-2 pr-3">{children}</th>;
}
function Td({ children }) {
  return <td className="py-2 pr-3">{children}</td>;
}

function RecomputeButton({ onDone }) {
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (busy) return;
    const confirmed = window.confirm(
      "Recompute the deterministic exploitability score for every cached CVE? " +
      "This preserves the AI's reasoning prose and only updates the numeric block. " +
      "Safe to run on a live database. Continue?"
    );
    if (!confirmed) return;
    setBusy(true);
    try {
      const r = await adminRecomputeExploitability();
      toast.success("Exploitability recomputed", {
        description: `processed ${r.processed} . updated ${r.updated} . skipped ${r.skipped}${
          r.errors?.length ? ` . errors ${r.errors.length}` : ""
        }`,
      });
      if (onDone) onDone();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message;
      toast.error("Recompute failed", {
        description: `${status ? `${status} . ` : ""}${detail}`,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={run}
      disabled={busy}
      data-testid="admin-recompute-exploitability"
      title="Re-run the deterministic exploitability scorer over every cached CVE row"
      className="flex items-center gap-1.5 text-xs font-mono text-slate-700 hover:text-amber-700 border border-slate-200 hover:border-amber-300 rounded-md px-3 py-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <Calculator className="h-3.5 w-3.5" />
      {busy ? "Recomputing..." : "Recompute scores"}
    </button>
  );
}

// ISO-3166 alpha-2 -> flag emoji (XX = unknown -> globe)
function flagFor(cc) {
  if (!cc || cc === "XX" || cc.length !== 2) return "[?]";
  const A = 0x1f1e6;
  const a = "A".charCodeAt(0);
  const up = cc.toUpperCase();
  return String.fromCodePoint(A + (up.charCodeAt(0) - a)) + String.fromCodePoint(A + (up.charCodeAt(1) - a));
}

function VisitsPanel({ visits }) {
  if (!visits) return null;
  const top = visits.top_countries || [];
  const max = Math.max(1, ...top.map((r) => r.count));
  return (
    <section className="panel p-5 md:p-7" data-testid="admin-visits">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="overline flex items-center gap-2">
            <Globe className="h-3 w-3 text-violet-700" />
            Visitor geography . last {visits.window_days}d
          </div>
          <h3 className="font-display text-xl text-slate-900 mt-1">
            Where the traffic comes from
          </h3>
        </div>
        <div className="flex flex-wrap gap-4 text-xs font-mono text-slate-600">
          <span>events: <span className="text-slate-900">{visits.total_events}</span></span>
          <span>distinct visitors: <span className="text-slate-900">{visits.distinct_visitors}</span></span>
          <span>countries: <span className="text-slate-900">{visits.distinct_countries}</span></span>
        </div>
      </div>

      {top.length === 0 ? (
        <p className="text-xs text-slate-500 font-mono">No visits captured yet.</p>
      ) : (
        <ul className="space-y-1.5" data-testid="admin-visits-list">
          {top.slice(0, 15).map((r) => {
            const pct = Math.round((r.count / max) * 100);
            return (
              <li key={r.country} className="flex items-center gap-3 text-xs">
                <span className="text-base w-7 text-center">{flagFor(r.country)}</span>
                <span className="font-mono text-slate-700 w-12">{r.country || "XX"}</span>
                <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-violet-600"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="font-mono text-slate-900 w-12 text-right">{r.count}</span>
                <span className="font-mono text-slate-500 w-20 text-right">
                  {r.distinct_ips} ip{r.distinct_ips === 1 ? "" : "s"}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function FeedbackTable({ rows }) {
  return (
    <section className="panel p-5" data-testid="admin-feedback-table">
      <div className="overline mb-3 flex items-center gap-2">
        <MessageSquare className="h-3 w-3 text-violet-700" />
        User feedback . newest {rows.length}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-slate-500">
            <tr className="border-b border-slate-100">
              <Th>When</Th>
              <Th>Country</Th>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Message</Th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} className="py-4 text-slate-500 font-mono">- no feedback yet -</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-100 align-top">
                <Td><span className="font-mono text-slate-600">{fmtDate(r.created_at)}</span></Td>
                <Td>
                  <span className="font-mono text-slate-700">
                    {flagFor(r.country)} {r.country || "XX"}
                  </span>
                </Td>
                <Td><span className="font-mono text-slate-700">{r.name || "-"}</span></Td>
                <Td><span className="font-mono text-violet-700">{r.email || "-"}</span></Td>
                <Td>
                  <span className="text-slate-800 whitespace-pre-wrap break-words block max-w-[640px]">
                    {r.message}
                  </span>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}


function ApiKeysPanel() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [label, setLabel] = useState("");
  const [rpm, setRpm] = useState(60);
  const [creating, setCreating] = useState(false);
  const [justCreated, setJustCreated] = useState(null); // {raw_key, prefix, label}
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminListApiKeys();
      setKeys(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error("Couldn't load API keys", {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!label.trim()) {
      toast.error("Label is required");
      return;
    }
    setCreating(true);
    try {
      const res = await adminCreateApiKey(label.trim(), Number(rpm) || 60);
      setJustCreated(res);
      setLabel("");
      setRpm(60);
      setShowNew(false);
      await load();
      toast.success("API key created", {
        description: `${res.prefix}… · copy it NOW; it won't be shown again.`,
      });
    } catch (err) {
      toast.error("Create failed", {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setCreating(false);
    }
  };

  const onRevoke = async (k) => {
    if (!window.confirm(`Revoke "${k.label}" (${k.prefix})? This can't be undone.`))
      return;
    try {
      await adminRevokeApiKey(k.id);
      toast.success("Key revoked");
      await load();
    } catch (err) {
      toast.error("Revoke failed", {
        description: err?.response?.data?.detail || err?.message,
      });
    }
  };

  const copyKey = async () => {
    if (!justCreated?.raw_key) return;
    try {
      await navigator.clipboard.writeText(justCreated.raw_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <section className="panel p-5 md:p-6" data-testid="admin-api-keys-panel">
      <div className="flex items-center gap-3 mb-4">
        <Key className="h-4 w-4 text-violet-700" />
        <h2 className="font-display font-bold text-lg md:text-xl">
          API keys for SBOM automation
        </h2>
        <div className="flex-1" />
        <span className="font-mono text-xs text-slate-500">
          {keys.length} total
        </span>
        <button
          onClick={() => setShowNew((s) => !s)}
          data-testid="admin-api-key-new"
          className="flex items-center gap-1.5 text-xs font-mono text-white bg-violet-600 hover:bg-violet-700 rounded-md px-3 py-1.5 transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
          {showNew ? "Cancel" : "New key"}
        </button>
      </div>

      {justCreated && (
        <div
          className="mb-4 bg-emerald-50 border border-emerald-200 rounded-md p-4"
          data-testid="admin-api-key-reveal"
        >
          <div className="overline mb-1 text-emerald-700">
            New key — copy now (won't be shown again)
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <code
              className="font-mono text-xs md:text-sm text-emerald-800 bg-slate-50 border border-emerald-200 rounded-md px-3 py-2 break-all flex-1 min-w-0"
              data-testid="admin-api-key-value"
            >
              {justCreated.raw_key}
            </code>
            <button
              onClick={copyKey}
              className="flex items-center gap-1 text-xs font-mono text-emerald-700 border border-emerald-200 hover:bg-emerald-100 rounded-md px-3 py-2 transition-colors"
              data-testid="admin-api-key-copy"
            >
              {copied ? (
                <>
                  <Check className="h-3.5 w-3.5" /> copied
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" /> copy
                </>
              )}
            </button>
            <button
              onClick={() => setJustCreated(null)}
              className="text-xs font-mono text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-400 rounded-md px-2 py-2 transition-colors"
            >
              dismiss
            </button>
          </div>
        </div>
      )}

      {showNew && (
        <form
          onSubmit={onCreate}
          className="mb-4 bg-slate-50 border border-slate-200 rounded-md p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end"
          data-testid="admin-api-key-form"
        >
          <div className="md:col-span-2">
            <label className="overline block mb-1">Label</label>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value.slice(0, 80))}
              placeholder="ci-pipeline-prod"
              data-testid="admin-api-key-label"
              className="w-full bg-transparent border border-slate-300 rounded-md px-3 py-2 font-mono text-sm text-slate-900 placeholder:text-slate-500 focus:outline-none focus:border-violet-600 focus:ring-1 focus:ring-violet-200"
            />
          </div>
          <div>
            <label className="overline block mb-1">Rate limit (req/min)</label>
            <input
              type="number"
              min={1}
              max={600}
              value={rpm}
              onChange={(e) => setRpm(e.target.value)}
              data-testid="admin-api-key-rpm"
              className="w-full bg-transparent border border-slate-300 rounded-md px-3 py-2 font-mono text-sm text-slate-900 focus:outline-none focus:border-violet-600 focus:ring-1 focus:ring-violet-200"
            />
          </div>
          <button
            type="submit"
            disabled={creating}
            data-testid="admin-api-key-create"
            className="h-10 bg-slate-900 text-white px-4 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors disabled:opacity-50"
          >
            {creating ? "Creating…" : "Create"}
          </button>
        </form>
      )}

      {loading ? (
        <div className="text-xs text-slate-500 font-mono">Loading…</div>
      ) : keys.length === 0 ? (
        <div className="text-xs text-slate-500">
          No keys yet. Create one to let CI pipelines bypass the public rate
          limit.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left">
                <Th>Label</Th>
                <Th>Prefix</Th>
                <Th>Rate / min</Th>
                <Th>Calls</Th>
                <Th>Last used</Th>
                <Th>Created</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr
                  key={k.id}
                  className="border-t border-slate-100"
                  data-testid={`admin-api-key-row-${k.prefix}`}
                >
                  <Td>{k.label}</Td>
                  <Td>
                    <code className="font-mono text-violet-700">{k.prefix}…</code>
                  </Td>
                  <Td>{k.rate_limit_per_min}</Td>
                  <Td>{k.total_calls}</Td>
                  <Td>
                    <span className="font-mono text-[10px] text-slate-500">
                      {k.last_used_at ? fmtDate(k.last_used_at) : "never"}
                    </span>
                  </Td>
                  <Td>
                    <span className="font-mono text-[10px] text-slate-500">
                      {fmtDate(k.created_at)}
                    </span>
                  </Td>
                  <Td>
                    {k.enabled ? (
                      <span className="font-mono text-[10px] text-emerald-700">
                        active
                      </span>
                    ) : (
                      <span className="font-mono text-[10px] text-red-700">
                        revoked
                      </span>
                    )}
                  </Td>
                  <Td>
                    {k.enabled && (
                      <button
                        onClick={() => onRevoke(k)}
                        data-testid={`admin-api-key-revoke-${k.prefix}`}
                        className="inline-flex items-center gap-1 text-[10px] font-mono text-red-700 hover:text-red-700 border border-red-200 hover:border-red-300 rounded-sm px-1.5 py-0.5 transition-colors"
                      >
                        <Trash2 className="h-3 w-3" /> revoke
                      </button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
